import React from 'react';
import { Button, Flag, Heading, Message, ProgressTracker, ProgressTrackerStep } from '@lumx/react';
import { mdiCheckCircleOutline } from '@mdi/js';
import MdiIcon from '@mdi/react';
import { useWizard } from '../context/WizardContext';

const MigrationProgressPage: React.FC = () => {
  const { sitesAssigned, sitesTotal, testConductedDate, testStats, deploymentScheduled, deploymentDate, navigateTo } = useWizard();

  const sitesComplete = sitesTotal > 0 && sitesAssigned === sitesTotal;
  const mobileConfigsTotal = 5;
  const mobileConfigsAssigned = sitesComplete ? mobileConfigsTotal : 0;

  const fmtDate = (d: Date) => {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  };

  const fmtDateTime = (d: Date) => {
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return `${fmtDate(d)}, ${time}`;
  };

  const siteMappingComplete = sitesComplete;
  const siteMappingActive   = !sitesComplete && sitesAssigned > 0;

  const testDeploymentComplete = testStats.completed > 0;
  const testDeploymentActive   = testStats.completed === 0 && testStats.running > 0;

  const deploymentComplete  = deploymentScheduled;
  const deploymentActive    = !deploymentScheduled && testDeploymentComplete;

  return (
    <div className="main-content" style={{ overflow: 'auto' }}>
      <div className="page-header">
        <div className="page-header-left">
          <Heading as="h1" typography="headline">Migration progress</Heading>
        </div>
      </div>

      <div style={{ padding: '16px 24px 0' }}>
        <Message kind="info" hasBackground>
          In order to migrate your organizational structure to the new governance model, you have to follow the steps below by <strong>1 January 2027</strong>. If you have any doubts, don't hesitate to reach out to your Customer Success Manager.
        </Message>
      </div>

      <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--color-border)' }}>
        <ProgressTracker aria-label="Migration progress steps">
          <ProgressTrackerStep
            label="Org mapping"
            isComplete={siteMappingComplete}
            isActive={siteMappingActive}
          />
          <ProgressTrackerStep
            label="Test deployment"
            isComplete={testDeploymentComplete}
            isActive={testDeploymentActive}
          />
          <ProgressTrackerStep
            label="Deployment"
            isComplete={deploymentComplete}
            isActive={deploymentActive}
          />
        </ProgressTracker>
      </div>

      <div style={{ padding: '0 24px' }}>

        {/* Step 1 — Org mapping */}
        <div style={{ maxWidth: 860, padding: '24px 0 8px' }}>
          <div className="mp-step">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Heading as="h2" typography="title">1. Org mapping</Heading>
              <MdiIcon path={mdiCheckCircleOutline} size={0.85} color={siteMappingComplete ? 'var(--lumx-color-green-N)' : 'rgba(0,0,0,0.38)'} />
              <Flag
                color={sitesAssigned === sitesTotal && sitesTotal > 0 ? 'green' : sitesAssigned < 1 ? 'grey' : 'yellow'}
                label={`${sitesAssigned}/${sitesTotal || 5} Sites mapped`}
              />
              <Flag
                color={mobileConfigsAssigned === mobileConfigsTotal ? 'green' : mobileConfigsAssigned < 1 ? 'grey' : 'yellow'}
                label={`${mobileConfigsAssigned}/${mobileConfigsTotal} Mobile configs mapped`}
              />
            </div>
            <div className="mp-step__body">
              <p>Map sites and mobile configurations to Org units &amp; Locations.</p>
              <Button emphasis="medium" onClick={() => navigateTo('site-mapping')}>
                Go to Org mapping
              </Button>
            </div>
          </div>
        </div>

        <div className="mp-step-divider" />

        {/* Step 2 — Test deployment */}
        <div style={{ maxWidth: 860, padding: '8px 0' }}>
          <div className="mp-step">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Heading as="h2" typography="title">2. Test deployment</Heading>
              <MdiIcon path={mdiCheckCircleOutline} size={0.85} color={testDeploymentComplete ? 'var(--lumx-color-green-N)' : 'rgba(0,0,0,0.38)'} />
              {testStats.completed > 0 ? (
                <Flag color="green" label={`${testStats.completed} test${testStats.completed > 1 ? 's' : ''} completed`} />
              ) : testStats.running > 0 ? (
                <Flag color="yellow" label={`${testStats.running} test${testStats.running > 1 ? 's' : ''} running`} />
              ) : (
                <Flag color="grey" label="No test conducted" />
              )}
            </div>
            <div className="mp-step__body">
              <p>Deploy a subset of your org units and locations to validate the configuration. Invite colleagues to help test. You can return to step 1 to make changes at any time.</p>
              <Button emphasis="medium" onClick={() => navigateTo('test-deployment')}>
                Go to Test deployment
              </Button>
            </div>
          </div>
        </div>

        <div className="mp-step-divider" />

        {/* Step 3 — Deployment */}
        <div style={{ maxWidth: 860, padding: '8px 0 24px' }}>
          <div className="mp-step">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Heading as="h2" typography="title">3. Deployment</Heading>
              <MdiIcon path={mdiCheckCircleOutline} size={0.85} color={deploymentComplete ? 'var(--lumx-color-green-N)' : 'rgba(0,0,0,0.38)'} />
              {testConductedDate === null && (
                <Flag color="grey" label="Start once test deployment is done" />
              )}
              {testConductedDate !== null && !deploymentScheduled && (
                <span className="mp-tag mp-tag--warning">NO DEPLOYMENT SCHEDULED</span>
              )}
              {deploymentScheduled && deploymentDate && (
                <span className="mp-tag mp-tag--success">SCHEDULED {fmtDate(deploymentDate).toUpperCase()}</span>
              )}
            </div>
            <div className="mp-step__body">
              <p>Schedule the deployment of your migration (needs at least 2 weeks notice).</p>
              <Button emphasis="medium" onClick={() => navigateTo('deployment')}>
                Go to Deployment
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default MigrationProgressPage;
